import { computed, inject, Injectable, signal, DOCUMENT } from '@angular/core';
import { environment } from '@environments/environment';
import { MenuUrl } from '@type/menu.type';
import { firstValueFrom } from 'rxjs';
import { UserSettingsService } from '../user-settings/user-settings.service';
import { SNOW_PARTICLES_CONFIG } from './particles-config.const';

/**
 * Service for snow effect
 *
 * Usually all injections of this service are commented out and only used during advent season.
 * To find all advent occurrences in the codebase search for `#search-advent` comment.
 *
 * For debug purposes you can enable advent season manually by calling `window['enableAdventSeason']()` in browser console.
 *
 * To start new season:
 * - uncomment usages of service
 * - update CURRENT_SEASON_START & CURRENT_SEASON_END vars in this file
 */

// @ts-ignore
declare const particlesJS: any;
// @ts-ignore
declare const pJSDom: null | any[];

const CDN_URL = 'https://cdn.jsdelivr.net/npm/particles.js@2.0.0/particles.min.js';
const CONTAINER_CLASS = 'app-snow-container';
const CONTAINER_ID = 'app-snow-container';
const CONTAINER_CLASS_MODIFIERS = {
  full: 'app-snow-container_full-size',
  sidebar: 'app-snow-container_sidebar-only',
};

const SHOW_MODE_SETTINGS_KEY = 'advent_snow_mode';

// starts on 1 Dec 2025 (month are numbered from 0)
const CURRENT_SEASON_START = new Date(2025, 11, 1);

// ends on 14 Jan 2026 at 00:00
const CURRENT_SEASON_END = new Date(2026, 0, 14);

const NEW_YEAR_DATE = new Date(CURRENT_SEASON_END.getFullYear(), 0, 1);

export type SnowMode = 'full' | 'sidebar' | false | null;

@Injectable({
  providedIn: 'root',
})
export class SnowService {
  protected defaultView = inject(DOCUMENT).defaultView;

  protected userSettingsService = inject(UserSettingsService);

  protected _debug = false;

  protected snowMode = signal<SnowMode>(null);

  isSeasonActive = signal(false);

  menuShowMode = computed<MenuUrl | null>(() => {
    if (!this.isSeasonActive()) {
      return null;
    }

    if (this.snowMode() === 'full') {
      return {
        name: '',
        url: '',
        icon: 'snowflake',
        onClick: () => void this.switchSnowModeToSidebar(),
      };
    } else if (this.snowMode() === 'sidebar') {
      return {
        name: '',
        url: '',
        icon: 'snowflake-half',
        onClick: () => void this.stopSnow(),
      };
    } else {
      return {
        name: '',
        url: '',
        icon: 'snowflake-crossed',
        onClick: () => void this.letItSnow('full'),
      };
    }
  });

  constructor() {
    this.initDevelopManualMode();
    void this.init();
  }

  protected isCurrentSeasonActive() {
    const today = new Date();

    if (this._debug) {
      return true;
    }

    return today >= CURRENT_SEASON_START && today <= CURRENT_SEASON_END;
  }

  async init() {
    this.isSeasonActive.set(this.isCurrentSeasonActive());

    if (!this.isSeasonActive()) {
      return;
    }

    let snowMode = await this.getSnowMode();

    if (snowMode === null) {
      snowMode = 'full';
    }

    if (snowMode === 'full') {
      await this.letItSnow('full');
    } else if (snowMode === 'sidebar') {
      await this.letItSnow('sidebar');
    }
  }

  getDaysUntilNewYear(): number {
    const today = new Date();
    const timeDiff = NEW_YEAR_DATE.getTime() - today.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24));
  }

  initDevelopManualMode() {
    if (environment.production) {
      return;
    }
    // @ts-ignore
    window['enableAdventSeason'] = () => {
      this._debug = true;
      void void this.init();
    };
  }

  async letItSnow(mode: SnowMode) {
    if (!this.defaultView || !mode) {
      return;
    }

    try {
      await this.loadScript();
      this.injectContainer(mode);
      void this.initParticles();
      if (mode != this.snowMode()) {
        void this.saveSnowMode(mode);
      }
    } catch (e) {
      /* empty */
    }
  }

  async switchSnowModeToSidebar() {
    void this.saveSnowMode('sidebar');
    const container = this.defaultView!.document.getElementById(CONTAINER_ID);
    container?.classList.add(CONTAINER_CLASS_MODIFIERS.sidebar);
    window.dispatchEvent(new Event('resize'));
  }

  async stopSnow() {
    if (!this.defaultView) {
      return;
    }
    await this.saveSnowMode(false);

    location.reload();
  }

  protected async saveSnowMode(mode: SnowMode) {
    try {
      this.snowMode.set(mode);
      await firstValueFrom(this.userSettingsService.save(SHOW_MODE_SETTINGS_KEY, this.snowMode()));
    } catch (e) {
      /* empty */
    }
  }

  protected async getSnowMode(): Promise<SnowMode> {
    try {
      return await firstValueFrom(this.userSettingsService.get<SnowMode>(SHOW_MODE_SETTINGS_KEY, null));
    } catch (e) {
      /* empty */
    }
    return null;
  }

  protected injectContainer(mode: SnowMode) {
    const snowContainer = this.defaultView!.document.createElement('div');
    snowContainer.classList.add(CONTAINER_CLASS);
    snowContainer.id = CONTAINER_ID;

    const modifierClass = mode ? CONTAINER_CLASS_MODIFIERS[mode] : '';
    if (modifierClass) {
      snowContainer.classList.add(modifierClass);
    }

    this.defaultView!.document.body.append(snowContainer);
  }

  protected loadScript() {
    return new Promise<void>((resolve, reject) => {
      const script = this.defaultView!.document.createElement('script');
      script.src = CDN_URL;
      script.type = 'text/javascript';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(`Failed to load script: ${CDN_URL}`);
      this.defaultView!.document.body.appendChild(script);
    });
  }

  protected initParticles() {
    particlesJS(CONTAINER_ID, SNOW_PARTICLES_CONFIG);
  }
}
